// Kapazitäten — Wochen-Grid (Abteilung × KW)
// A2 Feature: Soll/Ist-Stunden je Abteilung pro KW, Farbcode nach Auslastung.

import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, type KapazitaetOverview, type KapazitaetZelle } from '../lib/api';
import { ChevronLeft, ChevronRight, X, Save, Info } from 'lucide-react';
import { useEscClose } from '../hooks/useEscClose';

function nowYearWeek(): { jahr: number; kw: number } {
  const d = new Date();
  const onejan = new Date(d.getFullYear(), 0, 1);
  const kw = Math.ceil((((d.getTime() - onejan.getTime()) / 86400000) + onejan.getDay() + 1) / 7);
  return { jahr: d.getFullYear(), kw };
}

function cellColor(percent: number | null, isDefault: boolean): string {
  // Manuelle Sollwerte = volle Sättigung; Default-Werte = leicht abgeschwächt (dashed border)
  if (percent === null) return 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100';
  const base = isDefault ? 'border-dashed' : '';
  if (percent < 80)  return `bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 ${base}`;
  if (percent < 100) return `bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 ${base}`;
  if (percent < 120) return `bg-orange-100 text-orange-900 border-orange-300 hover:bg-orange-200 ${base}`;
  return `bg-red-100 text-red-900 border-red-400 hover:bg-red-200 ${base}`;
}

export default function Kapazitaeten() {
  const now = useMemo(nowYearWeek, []);
  const [jahr, setJahr] = useState<number>(now.jahr);
  const [kwStart, setKwStart] = useState<number>(Math.max(1, now.kw));
  const count = 8;

  const [data, setData] = useState<KapazitaetOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [modal, setModal] = useState<{ abteilungId: number; abteilungName: string; kw: number } | null>(null);

  const reload = () => {
    setLoading(true);
    api.kapazitaet
      .overview(jahr, kwStart, count)
      .then(setData)
      .catch((e) => setErr(e instanceof ApiError ? `API ${e.status}` : 'Fehler'))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [jahr, kwStart]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Kapazitäten</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setJahr(now.jahr); setKwStart(now.kw); }}
            className="px-3 py-1.5 rounded-md bg-brand text-white text-sm hover:bg-brand-dark"
            title="Zur aktuellen KW"
          >
            Heute
          </button>
          <button
            onClick={() => setKwStart(Math.max(1, kwStart - 4))}
            className="p-2 rounded border border-slate-200 hover:bg-slate-50"
            aria-label="4 Wochen zurück"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-slate-700 min-w-[160px] text-center tabular-nums">
            KW {kwStart}–{kwStart + count - 1} / {jahr}
          </span>
          <button
            onClick={() => setKwStart(Math.min(53 - count + 1, kwStart + 4))}
            className="p-2 rounded border border-slate-200 hover:bg-slate-50"
            aria-label="4 Wochen vor"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <select
            value={jahr}
            onChange={(e) => setJahr(parseInt(e.target.value))}
            className="text-sm border border-slate-200 rounded px-2 py-1.5"
          >
            {[now.jahr - 1, now.jahr, now.jahr + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 text-xs text-slate-600 bg-blue-50 border border-blue-200 p-3 rounded-md">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand" />
        <span>
          <strong>Ist</strong> = SUM(zeit_pro_stueck × stueck) offener Aufträge, nach Lieferdatum-KW.{' '}
          <strong>Soll*</strong> = Engpass-Anzahl × Std/Werker × Effizienz (Standard pro Abteilung).{' '}
          Zelle klicken um wochenspezifisch zu überschreiben (z.B. Urlaub, Zusatzschicht).
        </span>
      </div>

      {loading && <div className="text-slate-500 text-sm">Lädt …</div>}
      {err && <div className="text-red-600 text-sm">Fehler: {err}</div>}

      {data && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-slate-700 sticky left-0 bg-slate-50 min-w-[180px]">
                  Abteilung
                </th>
                {data.weeks.map((kw) => {
                  const isCurrent = jahr === now.jahr && kw === now.kw;
                  return (
                    <th
                      key={kw}
                      className={`px-2 py-2 font-semibold text-center min-w-[96px] ${
                        isCurrent ? 'bg-blue-100 text-blue-900' : 'text-slate-700'
                      }`}
                    >
                      KW {kw}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.data.map((row) => (
                <tr key={row.abteilung_id}>
                  <td
                    className="px-4 py-2 font-medium text-slate-800 sticky left-0 bg-white border-r border-slate-200"
                    style={{ borderLeft: `4px solid ${row.abteilung_farbe}` }}
                  >
                    {row.abteilung_name}
                  </td>
                  {row.wochen.map((w) => (
                    <td key={w.kw} className="p-1">
                      <button
                        onClick={() =>
                          setModal({
                            abteilungId: row.abteilung_id,
                            abteilungName: row.abteilung_name,
                            kw: w.kw,
                          })
                        }
                        className={`w-full rounded border px-2 py-1.5 text-left transition hover:shadow-sm ${cellColor(
                          w.last_percent,
                          !!w.is_default
                        )}`}
                        title={
                          w.has_soll
                            ? `Soll ${w.soll}h (manuell) · Ist ${w.ist}h · ${w.last_percent ?? '—'}%`
                            : `Soll ${w.soll}h (Standard) · Ist ${w.ist}h · ${w.last_percent ?? '—'}% — klicken zum Anpassen`
                        }
                      >
                        <div className="flex items-baseline justify-between gap-1">
                          <span className="font-semibold tabular-nums text-xs">
                            {w.ist}/{w.soll}{w.is_default ? '*' : ''}
                          </span>
                          <span className="text-[10px] uppercase tracking-wide">
                            {w.last_percent !== null ? `${w.last_percent}%` : ''}
                          </span>
                        </div>
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap gap-3 px-4 py-3 text-xs border-t border-slate-200 text-slate-600">
            <Legende color="bg-emerald-100 border-emerald-300" label="< 80 %" />
            <Legende color="bg-amber-100 border-amber-300" label="80-99 %" />
            <Legende color="bg-orange-100 border-orange-300" label="100-119 %" />
            <Legende color="bg-red-100 border-red-400" label="≥ 120 %" />
            <span className="inline-flex items-center gap-1.5 text-slate-500">
              <span className="inline-block w-4 h-4 rounded border border-slate-300 border-dashed bg-slate-50" />
              * = Standard-Soll (nicht manuell überschrieben)
            </span>
          </div>
        </div>
      )}

      {modal && (
        <ZelleModal
          abteilungId={modal.abteilungId}
          abteilungName={modal.abteilungName}
          jahr={jahr}
          kw={modal.kw}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function Legende({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded border ${color}`} />
      {label}
    </span>
  );
}

function ZelleModal({
  abteilungId,
  abteilungName,
  jahr,
  kw,
  onClose,
  onSaved,
}: {
  abteilungId: number;
  abteilungName: string;
  jahr: number;
  kw: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [zelle, setZelle] = useState<KapazitaetZelle | null>(null);
  const [soll, setSoll] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscClose(onClose);

  useEffect(() => {
    api.kapazitaet
      .cell(abteilungId, jahr, kw)
      .then((r) => {
        setZelle(r.data);
        // Wenn manuell gesetzt → benutze diesen Wert; sonst Default aus Abteilungs-Stamm
        const initialSoll = r.data.has_manual_soll
          ? r.data.stunden_soll
          : (r.data.default_soll ?? r.data.stunden_soll ?? 40);
        setSoll(String(initialSoll));
        setNotes(r.data.notes ?? '');
      })
      .catch(() => setError('Laden fehlgeschlagen'));
  }, [abteilungId, jahr, kw]);

  const save = async () => {
    const parsed = parseFloat(soll.replace(',', '.'));
    if (isNaN(parsed) || parsed < 0 || parsed > 400) {
      setError('Soll muss zwischen 0 und 400 liegen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.kapazitaet.upsert(abteilungId, jahr, kw, {
        stunden_soll: parsed,
        notes: notes || null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? `Fehler ${e.status}` : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const ist = zelle?.ist_stunden ?? 0;
  const percent = zelle?.last_percent ?? null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">
            {abteilungName} · KW {kw} / {jahr}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded p-3 border border-slate-200">
              <div className="text-[11px] uppercase text-slate-500 font-semibold">Ist (offene Aufträge)</div>
              <div className="text-2xl font-bold text-slate-800 tabular-nums">{ist.toFixed(1)} h</div>
            </div>
            <div className="bg-slate-50 rounded p-3 border border-slate-200">
              <div className="text-[11px] uppercase text-slate-500 font-semibold">Auslastung</div>
              <div className="text-2xl font-bold text-slate-800 tabular-nums">
                {percent !== null ? `${percent}%` : '—'}
              </div>
            </div>
          </div>

          {zelle && (zelle.engpass_anzahl ?? 0) > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-slate-700">
              <div className="font-semibold text-blue-900 mb-1">Standard-Kapazität dieser Abteilung</div>
              <div className="tabular-nums">
                {zelle.engpass_anzahl} × {zelle.engpass_typ} × {zelle.stunden_pro_einheit}h × {zelle.effizienz_faktor} (Effizienz)
                {' '}= <strong>{zelle.default_soll}h</strong>
              </div>
              {zelle.has_manual_soll && (
                <div className="text-amber-700 mt-1">
                  ⚠ Manuelle Override aktiv: {zelle.stunden_soll}h (statt Standard)
                </div>
              )}
            </div>
          )}

          <label className="block">
            <span className="text-sm text-slate-700 font-medium">
              Soll-Stunden {!zelle?.has_manual_soll && <span className="text-slate-400 font-normal">(wird Standard überschreiben)</span>}
            </span>
            <input
              type="number"
              step="0.5"
              min="0"
              max="400"
              value={soll}
              onChange={(e) => setSoll(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-brand"
              autoFocus
            />
            <span className="text-xs text-slate-500 mt-1 inline-block">0 – 400 h (halbe Stunden OK)</span>
          </label>

          <label className="block">
            <span className="text-sm text-slate-700 font-medium">Notiz (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="z.B. Urlaub, Feiertag, Zusatzschicht"
            />
          </label>

          {zelle?.updated_by_name && (
            <div className="text-xs text-slate-500">
              Zuletzt: {zelle.updated_by_name} · {zelle.updated_at}
            </div>
          )}
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-300 text-sm hover:bg-white">
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1.5 rounded bg-brand text-white text-sm font-medium hover:bg-brand-dark disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            <Save className="w-4 h-4" /> {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
